import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import IconLoader from "@/features/Common/components/IconLoader";
import SlideUpDialog from "@/features/Common/components/SlideUpDialog";
import { ApplyVoucherResponse } from "@/services/core-api-adapter";
import { convertPxToRem, formatDateToReadableString } from "@/utils";
import { Box, ListItem, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

interface VoucherCodeModalProps {
  isModalOpen: boolean;
  onModalClose: () => void;
  onContinueButtonClick: () => void;
  voucherDetails: ApplyVoucherResponse;
  onGoHomeButtonClick?: () => void;
}

export default function VoucherCodeModal({
  isModalOpen,
  onModalClose,
  onContinueButtonClick,
  voucherDetails,
  onGoHomeButtonClick,
}: VoucherCodeModalProps) {
  const { t } = useTranslation();

  const [shouldShowUseLaterInstructions, setShouldShowUseLaterInstructions] =
    useState(false);

  return (
    <SlideUpDialog
      isOpen={isModalOpen}
      onClose={() => onModalClose()}
      label={t("VoucherCodeModal.ariaLabel")}
    >
      {shouldShowUseLaterInstructions ? (
        <Stack textAlign="center" spacing={3}>
          <IconLoader
            icon="TagWithCheckIcon"
            color="primary"
            sx={{ fontSize: convertPxToRem(90), alignSelf: "center" }}
          />

          <Stack>
            <Typography variant="h3" fontWeight={600}>
              {t("VoucherCodeModal.useLater.title")}
            </Typography>
          </Stack>

          <Box
            src={`/components/VoucherCodeModal/Main-navigation.png`}
            component="img"
            loading="eager"
            alt={t("VoucherCodeModal.useLater.image.alt")}
          />

          <Stack direction="row" spacing={2}>
            <ButtonWithAnalytics
              onClick={onGoHomeButtonClick}
              intent="confirmational"
              page="VoucherCodeModal"
              text={t("common.goHomeButton")}
              fullWidth
              variant="outlined"
            >
              {t("common.goHomeButton")}
            </ButtonWithAnalytics>
          </Stack>
        </Stack>
      ) : (
        <Stack textAlign="center" spacing={3}>
          <IconLoader
            icon="TagWithCheckIcon"
            color="primary"
            sx={{ fontSize: convertPxToRem(90), alignSelf: "center" }}
          />

          <Stack spacing={1}>
            <Typography variant="h3" fontWeight={600}>
              <Trans
                i18nKey={
                  `VoucherCodeModal.${voucherDetails.memberOfferingUsage.serviceOfferingType}.title` as any
                }
              ></Trans>
            </Typography>
          </Stack>

          <Stack bgcolor="accent2.lightest" borderRadius={2} my={2} p={2}>
            <Stack component="ul" role="list">
              {voucherDetails && (
                <ListItem
                  role="listitem"
                  key={voucherDetails.memberOfferingUsage.id}
                  sx={{ padding: 0 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IconLoader icon="CheckVerifiedIcon" color="primary" />

                    <Typography variant="body2" color="neutral.main">
                      <Trans
                        i18nKey="VoucherCodeModal.validUntil.text"
                        values={{
                          date: formatDateToReadableString(
                            new Date(
                              voucherDetails.memberOfferingUsage.availableTo
                            )
                          ),
                        }}
                      ></Trans>
                    </Typography>
                  </Stack>
                </ListItem>
              )}
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2}>
            <ButtonWithAnalytics
              intent="confirmational"
              page="VoucherCodeModal"
              text={t("VoucherCodeModal.useLaterButton")}
              fullWidth
              variant="outlined"
              onClick={() => {
                setShouldShowUseLaterInstructions(true);
              }}
            >
              {t("VoucherCodeModal.useLaterButton")}
            </ButtonWithAnalytics>
            <ButtonWithAnalytics
              intent="confirmational"
              page="VoucherCodeModal"
              text={t("VoucherCodeModal.useNowButton")}
              fullWidth
              onClick={onContinueButtonClick}
            >
              {t("VoucherCodeModal.useNowButton")}
            </ButtonWithAnalytics>
          </Stack>
        </Stack>
      )}
    </SlideUpDialog>
  );
}
